// Pixel reveal for media: a cover of squares sits in front of the element (and
// everything inside it: media + hover media) and the squares disappear in a
// random order the first time it scrolls into view.
//
// Opt in with `data-pixel-reveal` on a positioned container. Options, all optional:
//   --pixel-color   (CSS var, default #fff) colour of the cover
//   data-pixel-cell (px, default 16)        size of a square
//   data-pixel-duration (ms, default 600)   length of the reveal
//   data-pixel-order    which squares go first (default "center"):
//                         "center"      radiates out from the middle
//                         "from-left" | "from-right" | "from-top" | "from-bottom"
//                                       sweeps in from that edge
//                         "random"      no pattern
//   data-pixel-jitter   (0-1, default 0.3) randomness mixed into the pattern, so
//                       the front is ragged rather than a clean line
//   data-pixel-mode="mosaic"   for images: instead of squares clearing off a cover, the image
//                       itself shows up as a coarse mosaic that sharpens step by step.
//                       Works with an <img>, a background image, or a <video> that already
//                       has a frame; anything else falls back to the squares cover.
//   data-pixel-desktop-only    no reveal on tablet/mobile: the element is shown as it is
//                       (its stylesheet has to drop the covered state there too)
//   data-pixel-reveal="manual"              not scroll-driven: the owning component
//                                           calls PixelReveal.reveal(el) itself
//
// Until JS takes over, a CSS cover (_pixel-reveal.scss) hides the media from
// first paint, so there is no flash of the bare media.

import { pageReady } from "../../utils/page-ready";
import { BP } from "../../utils/breakpoints";

const CELL = 4;
const DURATION = 600;

const jitterOf = (el) => (el.dataset.pixelJitter !== undefined ? Number(el.dataset.pixelJitter) : 0.3);

// Cell indices in the order they should disappear: the i-th one goes at i / n * duration.
function buildOrder(cols, rows, mode, jitter) {
  const order = Array.from({ length: cols * rows }, (_, i) => i);
  if (mode === "random") {
    for (let i = order.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [order[i], order[j]] = [order[j], order[i]];
    }
    return order;
  }

  // Sort key in 0..1: how far a cell is from where the reveal starts, blended with noise.
  const fx = (c) => (cols > 1 ? c / (cols - 1) : 0);
  const fy = (r) => (rows > 1 ? r / (rows - 1) : 0);
  const distance = {
    "from-left": (c) => fx(c),
    "from-right": (c) => 1 - fx(c),
    "from-top": (c, r) => fy(r),
    "from-bottom": (c, r) => 1 - fy(r),
    center: (c, r) => Math.min(1, Math.hypot(fx(c) - 0.5, fy(r) - 0.5) / Math.SQRT1_2),
  }[mode] || (() => 0);

  const key = order.map((i) => (1 - jitter) * distance(i % cols, Math.floor(i / cols)) + jitter * Math.random());
  return order.sort((a, b) => key[a] - key[b]);
}

// One rAF loop for every running reveal. Each frame, reveals starting in it are
// all measured first (layout reads), then all built (DOM writes): cards that
// start together (a grid scrolling in, a staggered batch) don't force a layout
// each. Then the running animations step.
const loop = {
  starts: [], // { measure() -> m, build(m, now) }
  steps: new Set(), // fn(now) -> true while running
  raf: 0,
  start(job) {
    this.starts.push(job);
    this.wake();
  },
  step(fn) {
    this.steps.add(fn);
    this.wake();
  },
  wake() {
    if (!this.raf) this.raf = requestAnimationFrame((now) => this.tick(now));
  },
  tick(now) {
    this.raf = 0;
    const starts = this.starts.splice(0);
    const measures = starts.map((job) => job.measure());
    starts.forEach((job, i) => job.build(measures[i], now));
    for (const fn of this.steps) if (!fn(now)) this.steps.delete(fn);
    if (this.steps.size || this.starts.length) this.wake();
  },
};

// overflow never changes on these containers: read once per element.
function clipsOf(el) {
  if (el.__pixelClips === undefined) {
    const cs = getComputedStyle(el);
    el.__pixelClips = cs.overflow === "hidden" || cs.overflowX === "hidden";
  }
  return el.__pixelClips;
}

// The reveal canvas: a small bitmap (one pixel per square) stretched to the
// element by the CSS, image-rendering: pixelated keeping the squares crisp.
function revealCanvas(el, clips, width, height) {
  const canvas = document.createElement("canvas");
  canvas.className = "pixel-reveal__canvas";
  canvas.setAttribute("aria-hidden", "true");
  canvas.width = width;
  canvas.height = height;
  // On a container that clips (overflow: hidden) the canvas bleeds 1px past every side,
  // which the clip trims: fractional layout sizes would otherwise leave a hairline of
  // the media at the edges. Without a clip that bleed would stick out of the media.
  if (clips) {
    canvas.style.inset = "-1px";
    canvas.style.width = "calc(100% + 2px)";
    canvas.style.height = "calc(100% + 2px)";
  }
  el.appendChild(canvas);
  return canvas;
}

// The CSS cover stays two more frames so the fresh canvas is composited first.
function uncoverSoon(el, run) {
  requestAnimationFrame(() =>
    requestAnimationFrame(() => el.__pixelRun === run && el.classList.add("is-revealed"))
  );
}

// Squares mode: a cover of squares that clear in the data-pixel-order pattern.
function cover(el, run, opts) {
  loop.start({
    measure: () => ({
      w: el.clientWidth,
      h: el.clientHeight,
      clips: clipsOf(el),
      color: getComputedStyle(el).getPropertyValue("--pixel-color").trim() || "#fff",
    }),
    build: ({ w, h, clips, color }) => {
      if (el.__pixelRun !== run) return;
      if (w < 1 || h < 1) {
        el.__pixelBusy = false;
        el.classList.add("is-revealed");
        return;
      }

      const cell = Number(el.dataset.pixelCell) || CELL;
      const duration = Number(el.dataset.pixelDuration) || DURATION;
      // Squares are `cell` px, give or take the fraction needed to fit a whole number of them.
      const cols = Math.ceil(w / cell);
      const rows = Math.ceil(h / cell);
      const canvas = revealCanvas(el, clips, cols, rows);
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = color;
      ctx.fillRect(0, 0, cols, rows);
      uncoverSoon(el, run);

      const order = buildOrder(cols, rows, opts.order || el.dataset.pixelOrder || "center", jitterOf(el));
      const total = order.length;
      let cleared = 0;
      let start = null;

      loop.step((now) => {
        if (el.__pixelRun !== run) return false; // reset() or a newer reveal() took over
        if (start === null) start = now;
        const upTo = Math.min(total, Math.ceil(((now - start) / duration) * total));
        for (; cleared < upTo; cleared++) {
          const idx = order[cleared];
          ctx.clearRect(idx % cols, Math.floor(idx / cols), 1, 1);
        }
        if (cleared < total) return true;
        canvas.remove();
        el.__pixelBusy = false;
        return false;
      });
    },
  });
}

// Puts an element back to its covered, not-yet-revealed state so reveal() can play
// again (e.g. a card that left the view). Cancels a reveal in progress.
function reset(el) {
  el.__pixelRun = (el.__pixelRun || 0) + 1;
  el.__pixelBusy = false;
  el.querySelectorAll(":scope > .pixel-reveal__canvas").forEach((c) => c.remove());
  el.classList.remove("is-revealed");
}

// Mosaic mode: the image appears as big blocks that get finer until it's the real image.
// Block size in px, coarse to fine. Desktop plays five steps; tablet/mobile three, over
// smaller blocks (media are smaller there: the coarsest desktop blocks would collapse
// into a single flat colour, and steps would look alike).
const MOSAIC_STEPS = [192, 96, 48, 24, 8];
const MOSAIC_STEPS_MOBILE = [96, 32, 8];
const MOSAIC_STEP_MS = 110;

// A <video> with a frame to draw: already there, or waited for a moment (a lazy video
// that hasn't started loading gives up, and the caller falls back to the squares cover).
function videoFrame(video, timeout = 800) {
  if (video.readyState >= 2) return Promise.resolve(video);
  return new Promise((resolve) => {
    const done = () => {
      video.removeEventListener("loadeddata", done);
      clearTimeout(timer);
      resolve(video.readyState >= 2 ? video : null);
    };
    const timer = setTimeout(done, timeout);
    video.addEventListener("loadeddata", done);
  });
}

function loadImageUrl(src) {
  return new Promise((resolve) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => resolve(null);
    i.src = src;
  });
}

// What to pixelate, in order: an <img>; a <video> that already has a frame buffered (a
// fast connection: use it live, no wait); a poster image (the Bunny Stream default
// thumbnail set as data-poster — see product-card.twig / cardMediaPool.js) so a
// still-loading video pixelates into a real picture instead of nothing; a video with no
// poster, waited on a little; a background-image layer; otherwise nothing (the caller
// falls back to the squares cover).
function findImage(el) {
  const img = el.querySelector("img");
  if (img) return img.decode ? img.decode().then(() => img).catch(() => img) : Promise.resolve(img);

  const video = el.querySelector("video");
  if (video?.readyState >= 2) return Promise.resolve(video);

  const poster = el.querySelector("[data-poster]")?.dataset.poster;
  if (poster) return loadImageUrl(poster);

  if (video) return videoFrame(video);

  for (const node of el.querySelectorAll("*")) {
    const m = getComputedStyle(node).backgroundImage.match(/url\(["']?(.*?)["']?\)/);
    if (m) return loadImageUrl(m[1]);
  }
  return Promise.resolve(null);
}

// Each step is a tiny bitmap stretched by the CSS, like the squares mode.
// An image is read once, downscaled to the finest step, and the coarser steps
// are derived from that small copy. A video keeps playing underneath, so each
// step reads its current frame instead: no jump to the live video at the end
// (one frame read per step).
function mosaic(el, run, opts) {
  findImage(el).then((img) => {
    if (el.__pixelRun !== run) return;
    const iw = img?.videoWidth || img?.naturalWidth;
    const ih = img?.videoHeight || img?.naturalHeight;
    if (!img || !iw) {
      // Nothing to pixelate (a video without a frame, a failed load): squares cover instead.
      el.__pixelBusy = false;
      reveal(el, { ...opts, mode: "cover" });
      return;
    }

    loop.start({
      measure: () => ({ w: el.clientWidth, h: el.clientHeight, clips: clipsOf(el) }),
      build: ({ w, h, clips }, now) => {
        if (el.__pixelRun !== run) return;
        if (w < 1 || h < 1) {
          el.__pixelBusy = false;
          reveal(el, { ...opts, mode: "cover" });
          return;
        }

        // Crop of the image that `object-fit: cover` would show in w x h.
        const scale = Math.max(w / iw, h / ih);
        const sw = w / scale;
        const sh = h / scale;
        const sx = (iw - sw) / 2;
        const sy = (ih - sh) / 2;

        const steps = window.matchMedia(BP.tabletAndMobile).matches ? MOSAIC_STEPS_MOBILE : MOSAIC_STEPS;
        const sizes = steps.map((block) => [Math.ceil(w / block), Math.ceil(h / block)]);

        // Image: coarse to fine levels, the finest being the only read of the source.
        let levels = null;
        if (!(img instanceof HTMLVideoElement)) {
          levels = sizes.map(([lw, lh]) => {
            const c = document.createElement("canvas");
            c.width = lw;
            c.height = lh;
            return c;
          });
          const finest = levels[levels.length - 1];
          const fctx = finest.getContext("2d");
          fctx.imageSmoothingQuality = "high";
          fctx.drawImage(img, sx, sy, sw, sh, 0, 0, finest.width, finest.height);
          for (let i = levels.length - 2; i >= 0; i--) {
            const lctx = levels[i].getContext("2d");
            lctx.imageSmoothingQuality = "high";
            lctx.drawImage(finest, 0, 0, levels[i].width, levels[i].height);
          }
        }

        const paint = (canvas, step) => {
          const [cw, ch] = sizes[step];
          canvas.width = cw;
          canvas.height = ch;
          const ctx = canvas.getContext("2d");
          ctx.imageSmoothingQuality = "high";
          if (levels) ctx.drawImage(levels[step], 0, 0);
          else ctx.drawImage(img, sx, sy, sw, sh, 0, 0, cw, ch); // the video's current frame
        };

        const canvas = revealCanvas(el, clips, ...sizes[0]);
        paint(canvas, 0);
        uncoverSoon(el, run);

        let shown = 0;
        loop.step((t) => {
          if (el.__pixelRun !== run) return false;
          const step = Math.floor((t - now) / MOSAIC_STEP_MS);
          if (step >= steps.length) {
            canvas.remove();
            el.__pixelBusy = false;
            return false;
          }
          if (step !== shown) {
            shown = step;
            paint(canvas, step);
          }
          return true;
        });
      },
    });
  });
}

// opts.order overrides data-pixel-order for this play.
function reveal(el, opts = {}) {
  if (el.classList.contains("is-revealed") || el.__pixelBusy) return;
  if (el.hasAttribute("data-pixel-desktop-only") && window.matchMedia(BP.tabletAndMobile).matches) {
    el.classList.add("is-revealed");
    return;
  }
  // Nothing plays behind the loader / page-transition overlay: wait for the page.
  if (!pageReady.isReady) {
    if (el.__pixelQueued) return;
    el.__pixelQueued = true;
    pageReady.ready.then(() => {
      el.__pixelQueued = false;
      reveal(el, opts);
    });
    return;
  }
  const run = (el.__pixelRun = (el.__pixelRun || 0) + 1);
  el.__pixelBusy = true;
  if ((opts.mode || el.dataset.pixelMode) === "mosaic") mosaic(el, run, opts);
  else cover(el, run, opts);
}

const targets = (root) => [
  ...(root.matches?.("[data-pixel-reveal]") ? [root] : []),
  ...root.querySelectorAll("[data-pixel-reveal]"),
];

export default class PixelReveal {
  static reveal = reveal;
  static reset = reset;

  // Cover the media inside `root` again so its reveal can play once more (a card that
  // is animated in a second time, e.g. after a shop filter).
  static restart(root) {
    targets(root).forEach(reset);
  }

  // For components that already own an entrance animation: take the covers inside
  // `root` out of the scroll-driven default (call before the reveal would fire,
  // e.g. in the constructor or right after injecting markup)...
  static claim(root) {
    targets(root).forEach((el) => (el.dataset.pixelReveal = "manual"));
  }

  // ...and clear them from that animation, optionally `delay` seconds in
  // (match the stagger of the tween it accompanies).
  static play(root, delay = 0, opts) {
    targets(root).forEach((el) => {
      if (delay > 0) setTimeout(() => reveal(el, opts), delay * 1000);
      else reveal(el, opts);
    });
  }

  static init() {
    // Tells the CSS the script is alive, which disarms its "never ran" fallback.
    document.documentElement.classList.add("pixel-ready");
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          io.unobserve(entry.target);
          // A component may have claimed it since we started observing (init runs
          // before the components do): its own animation plays the reveal then.
          if (entry.target.dataset.pixelReveal === "manual") continue;
          reveal(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    // A WeakSet, not a DOM marker: cloneNode() copies attributes, so components
    // that clone their cards (keen-slider loops, showcase) would hand the clones
    // a "seen" flag and they would never be observed.
    const seen = new WeakSet();
    const observe = (el) => {
      if (el.dataset.pixelReveal === "manual" || seen.has(el)) return;
      seen.add(el);
      io.observe(el);
    };
    const scan = (root) => {
      if (root.matches?.("[data-pixel-reveal]")) observe(root);
      root.querySelectorAll?.("[data-pixel-reveal]").forEach(observe);
    };

    // Reveals are gated by pageReady inside reveal(); observing can start right away.
    scan(document);
    // Cards added later (shop filtering, search results, slider clones).
    new MutationObserver((muts) => {
      for (const m of muts) m.addedNodes.forEach((n) => n.nodeType === 1 && scan(n));
    }).observe(document.body, { childList: true, subtree: true });
  }
}
